import React, { useState } from 'react';
import ConfirmModal from '@/stories/Components/ConfirmModal/ConfirmModal';
import TextArea from '@/UIlibrary/TextArea/TextArea';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { useTranslations } from 'use-intl';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import logger from '@/logger';
import toast from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import {
	RejectMicrocreditRequestInput,
	useRejectMicrocreditRequestMutation,
	RejectMicrocreditRequestMutation
} from '@/graphql/__generated__/credit-requests';

const RejectModal: React.FC<{
	isOpen: boolean;
	onClose: () => void;
	microcreditRequestId: string;
	onSuccess?: () => void;
}> = ({ isOpen, onClose, microcreditRequestId, onSuccess }) => {
	const t = useTranslations('MicrocreditRequestDetails');
	const [formData, setFormData] = useState<RejectMicrocreditRequestInput>({
		microcreditRequestId: '',
		rejectionReason: ''
	});
	const [formErrors, setFormErrors] = useState<Record<keyof RejectMicrocreditRequestInput, string>>(
		{
			microcreditRequestId: '',
			rejectionReason: ''
		}
	);

	const creditRequestsClient = useApolloClient(SCHEMAS.MICROCREDITS);

	const [rejectMicrocreditRequest, { loading }] = useRejectMicrocreditRequestMutation({
		client: creditRequestsClient,
		onCompleted: async (data: RejectMicrocreditRequestMutation) => {
			const rejectMicrocreditResult = data.rejectMicrocreditRequest;
			if (rejectMicrocreditResult?.successful) {
				logger.success('Microcredit request rejected successfully', rejectMicrocreditResult);
				toast.success(t('MicrocreditRejectedSuccessfully'));
				onClose();
				if (onSuccess) {
					onSuccess();
				}
			}
			if (
				rejectMicrocreditResult?.mutationErrors &&
				rejectMicrocreditResult.mutationErrors.length > 0
			) {
				let hasFieldErrors = false;

				rejectMicrocreditResult.mutationErrors.forEach((error) => {
					if (error) {
						Sentry.captureException(error);
						switch (error.__typename) {
							case 'InvalidInput':
								logger.error('Invalid input', error.field);
								if (error.field === 'rejection_reason') {
									setFormErrors((prev) => ({
										...prev,
										rejectionReason: error.message || t('RejectionReasonRequired')
									}));
									hasFieldErrors = true;
								}
								break;
							case 'ActionFailed':
								logger.error('Action failed', error.message);
								break;
							case 'InvalidAction':
								logger.error('Invalid action', error.message);
								break;
							default:
								logger.error('Unhandled mutation error', error);
								break;
						}
					}
				});

				if (!hasFieldErrors) {
					toast.error(t('ErrorRejectingMicrocredit'));
				}
			}
		},
		onError: (error) => {
			logger.error('Error rejecting microcredit', error);
			toast.error(t('ErrorRejectingMicrocredit'));
		}
	});

	// Set initial microcredit request ID
	React.useEffect(() => {
		if (microcreditRequestId) {
			setFormData((prev) => ({
				...prev,
				microcreditRequestId
			}));
		}
	}, [microcreditRequestId]);

	const handleRejectReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const value = e.target.value;
		setFormData((prev) => ({
			...prev,
			rejectionReason: value
		}));

		if (formErrors.rejectionReason) {
			setFormErrors((prev) => ({
				...prev,
				rejectionReason: ''
			}));
		}
	};

	const handleRejectMicrocredit = async () => {
		if (!formData.rejectionReason) {
			setFormErrors((prev) => ({
				...prev,
				rejectionReason: t('RejectionReasonRequired')
			}));
			return;
		}
		setFormErrors({
			microcreditRequestId: '',
			rejectionReason: ''
		});

		await rejectMicrocreditRequest({
			variables: {
				input: formData
			}
		});
	};

	const rejectionReasonHint =
		t('RejectionReasonLength') + '. ' + formData.rejectionReason.length + '/3400';

	return (
		<ConfirmModal
			isOpen={isOpen}
			confirmActionText={t('Reject')}
			title={t('RejectMicrocredit')}
			onClose={onClose}
			onCancel={onClose}
			onConfirm={handleRejectMicrocredit}
			maxWidth={600}
			subtitle={t('RejectMicrocreditSubtitle')}
			confirmBtnLoading={loading}
		>
			<div className="flex flex-col gap-1 py-4">
				<Typography size="md">{t('RejectReasonLabel')}</Typography>
				<div className="flex flex-col gap-1">
					<TextArea
						rows={4}
						value={formData.rejectionReason}
						onChange={handleRejectReasonChange}
						placeholder={t('EnterRejectReason')}
						error={!!formErrors.rejectionReason}
						hint={rejectionReasonHint}
						maxChars={3400}
						strictMaxChars={true}
					/>
					{formErrors.rejectionReason && (
						<Typography size="sm" color="error">
							{formErrors.rejectionReason}
						</Typography>
					)}
				</div>
			</div>
		</ConfirmModal>
	);
};

export default RejectModal;
